/**
 * App Context — updated with confirmedColumns state.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { UploadResponse, ProgressUpdate, JobStatus } from '../types';

interface AppState {
  uploadData: UploadResponse | null;
  selectedSheets: string[];
  confirmedColumns: Record<string, string[]> | null;
  currentJobId: string | null;
  jobStatus: JobStatus | null;
  progress: ProgressUpdate | null;

  setUploadData: (data: UploadResponse | null) => void;
  setSelectedSheets: (sheets: string[]) => void;
  toggleSheetSelection: (name: string) => void;
  setConfirmedColumns: (map: Record<string, string[]> | null) => void;
  setCurrentJobId: (id: string | null) => void;
  setJobStatus: (s: JobStatus | null) => void;
  updateProgress: (p: ProgressUpdate) => void;
  resetState: () => void;
}

const AppContext = createContext<AppState | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [uploadData, setUploadData] = useState<UploadResponse | null>(null);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [confirmedColumns, setConfirmedColumns] = useState<Record<string, string[]> | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);

  const toggleSheetSelection = useCallback((name: string) => {
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  }, []);

  const updateProgress = useCallback((p: ProgressUpdate) => {
    setProgress(p);
    if (p.current_status) setJobStatus(p.current_status as JobStatus);
  }, []);

  const resetState = useCallback(() => {
    setUploadData(null);
    setSelectedSheets([]);
    setConfirmedColumns(null);
    setCurrentJobId(null);
    setJobStatus(null);
    setProgress(null);
  }, []);

  return (
    <AppContext.Provider
      value={{
        uploadData,
        selectedSheets,
        confirmedColumns,
        currentJobId,
        jobStatus,
        progress,
        setUploadData,
        setSelectedSheets,
        toggleSheetSelection,
        setConfirmedColumns,
        setCurrentJobId,
        setJobStatus,
        updateProgress,
        resetState,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

export default AppContext;
